import { isEmpty } from 'lodash-es';
import type {
  EventPartScore,
  LatestOutcomeChanges,
  Market,
  OutcomeProperties,
  PublishedEventWithMarkets,
  WSMessagePayloadType,
} from '../@types';
import { MessageType } from '../hooks/useWebsocket';
import { updateEvent, updateEventPartScores } from '../utils';

type OutcomeUpdateFields = {
  odds?: string;
  isActive?: boolean;
  isAvailable?: boolean;
  properties?: OutcomeProperties;
  latestChanges?: LatestOutcomeChanges | null;
};

export type PublishedEventWithMarketsAndScores = PublishedEventWithMarkets & {
  eventPartScores?: EventPartScore[];
  mainEventPart?: EventPartScore | null;
};

const updateMarket = (market: Market, index: number, outcome: OutcomeUpdateFields) => {
  if (!market) {
    return;
  }

  market.outcomes[index] = {
    ...market.outcomes[index],
    ...outcome,
  };

  return market;
};

const getMarketIndex = (markets: Market[], id?: string) => {
  if (!id) {
    return -1;
  }

  return markets.findIndex((m) => m.id === id);
};

const eventUpdater = (
  prev: PublishedEventWithMarketsAndScores,
  next: MessageType<WSMessagePayloadType>,
  callback: () => void
): PublishedEventWithMarketsAndScores => {
  const { event, payload } = next;

  if (isEmpty(event) || isEmpty(payload)) {
    return prev;
  }

  if (next.event === 'OUTCOME') {
    if (prev.manualControl) return prev;

    const outcomeOddsUpdated = 'odds' in payload;
    const outcomeStatusUpdated = 'isActive' in payload;
    const outcomeAvailabilityUpdated = 'isAvailable' in payload;
    const outcomePropertiesUpdated = 'properties' in payload;

    const market = prev?.markets.find((m) => m.outcomes.some((o) => o.id === payload.id));

    if (market?.manualControl) return prev;

    const outcomeIndex = market?.outcomes.findIndex((o) => o.id === payload.id);
    const marketIndex = getMarketIndex(prev.markets, market?.id);

    if (outcomeIndex !== undefined && outcomeIndex !== -1 && market) {
      const updateData: OutcomeUpdateFields = {};

      if (outcomeOddsUpdated && payload.odds) {
        updateData.odds = payload.odds;

        const prevOdds = parseFloat(market.outcomes[outcomeIndex].odds || '0');
        const nextOdds = parseFloat(payload.odds || '0');
        const isBetable = market.outcomes[outcomeIndex].isActive && market.outcomes[outcomeIndex].isAvailable;

        if (prevOdds > 0 && nextOdds > 0 && prevOdds !== nextOdds && isBetable) {
          updateData.latestChanges = {
            ...(market.outcomes[outcomeIndex].latestChanges || {}),
            prevOdds: prevOdds,
            changedAt: Date.now(),
          };
        }
      }
      if (outcomeStatusUpdated) {
        updateData.isActive = payload.isActive;
      }
      if (outcomeAvailabilityUpdated) {
        updateData.isAvailable = payload.isAvailable;
      }
      if (outcomePropertiesUpdated && payload.properties) {
        updateData.properties = payload.properties;
      }

      if (Object.keys(updateData).length > 0) {
        const updatedMarket = updateMarket(market, outcomeIndex, updateData);

        if (updatedMarket) {
          prev.markets[marketIndex] = updatedMarket;
        }
      }
    }
  }

  if (next.event === 'EVENT') {
    prev = updateEvent(prev, payload) as PublishedEventWithMarketsAndScores;
  }

  if (next.event === 'MARKET') {
    const market = prev.markets?.find((m) => m.id === payload.id);
    if ('isActive' in payload && market) {
      market.isActive = payload.isActive || false;

      const marketIndex = getMarketIndex(prev.markets, market.id);

      if (marketIndex !== -1) {
        prev.markets[marketIndex] = market;
      }
    }
  }

  if (event === 'EVENT_PART_SCORE') {
    const { valueHome, valueAway, id } = payload;

    return {
      ...prev,
      mainEventPart:
        prev.mainEventPart?.id === id
          ? {
              ...prev.mainEventPart,
              valueHome: valueHome ?? prev.mainEventPart.valueHome,
              valueAway: valueAway ?? prev.mainEventPart.valueAway,
            }
          : prev.mainEventPart,
      eventPartScores: prev.eventPartScores
        ? updateEventPartScores(prev.eventPartScores, payload, callback)
        : prev.eventPartScores,
    };
  }

  return { ...prev };
};

export default eventUpdater;
